import React, {useEffect, useState} from 'react';
import { observer } from 'mobx-react-lite';
import SimpleTable from '../../components/SimpleTable';

import {ordersGrid} from './data';
import {Header, Loading} from '../../components';
import {ComboStore} from "../../stores/combo/comboStore";
import * as ComboProvider from '../../services/combo';
import {AccountStore} from "../../stores/account/accountStore";
import {Manager} from "../../core";
import {Colors} from "../../styles";
import {moneyFormat} from "../../utils";
import {RatingStore} from "../../stores/rating/ratingStore";
import {TeamStore} from "../../stores/team/teamStore";

export const Orders = observer(() => {
  const { list } = ComboStore();
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      await ComboStore().get();
      setLoading(false);
    } catch (e) {
      setLoading(false);
      Manager.RequestError('error.orders.handlerRequest', e);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleCancel = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!selectedOrder) return;
    try {
      if (!window.confirm('Tem certeza que deseja cancelar este pedido?')) return;
      setLoading(true);
      const props = { 
        id: selectedOrder.id, 
        userId: AccountStore().getUserId(), 
        property: 'status', 
        value: 'CANCELED' 
      } as any;
      await ComboProvider.update(props);
      await ComboStore().get();
      setSelectedOrder(null);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      Manager.RequestError('error.orders.cancel', e);
    }
  };

  const handleReactivate = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!selectedOrder) return;
    try {
      if (!window.confirm('Reativar este pedido?')) return;
      setLoading(true);
      const props = { 
        id: selectedOrder.id, 
        userId: AccountStore().getUserId(), 
        property: 'status', 
        value: 'ORDER' 
      } as any;
      await ComboProvider.update(props);
      await ComboStore().get();
      setSelectedOrder(null);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      Manager.RequestError('error.orders.reactivate', e);
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white dark:bg-secondary-dark-bg dark:text-gray-200 rounded-3xl relative">
      {loading && (
        <div className="absolute inset-0 bg-white dark:bg-secondary-dark-bg bg-opacity-50 dark:bg-opacity-80 z-50 flex items-center justify-center">
          <Loading backgroundColor={Colors.light} type={'bubbles'} color={Colors.green_darker} />
        </div>
      )}
      <Header category="Página" title="Pedidos" />
      <SimpleTable 
        columns={ordersGrid as any} 
        data={list} 
        pageSize={10} 
        onRowClick={(row) => setSelectedOrder(row)}
      />

      {selectedOrder && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
          onClick={() => setSelectedOrder(null)}
        >
          <div 
            className="bg-white dark:bg-[#33373E] p-6 rounded-2xl w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Detalhes do Pedido</h2>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl">&times;</button>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">ID do Plano</p>
                  <p className="text-lg text-gray-800 dark:text-gray-200">{selectedOrder.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Status</p>
                  <p className={`font-bold ${selectedOrder.status === 'CANCELED' ? 'text-red-500' : selectedOrder.status === 'DELIVERED' ? 'text-blue-500' : selectedOrder.status === 'PENDING' ? 'text-orange-500' : 'text-green-500'}`}>
                    {selectedOrder.status === 'DELIVERED' ? 'ENTREGUE' : selectedOrder.status === 'PENDING' ? 'PENDENTE' : selectedOrder.status}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Cliente</p>
                  <p className="text-lg text-gray-800 dark:text-gray-200 font-bold">{selectedOrder.name} {selectedOrder.surname}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Plano</p>
                  <p className="text-lg text-gray-800 dark:text-gray-200 font-bold">{selectedOrder.plan}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Valor</p>
                  <p className="text-lg text-gray-800 dark:text-gray-200">{moneyFormat(selectedOrder.value)}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Frequência</p>
                <p className="text-lg text-gray-800 dark:text-gray-200">{selectedOrder.often}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Dia da Semana</p>
                  <p className="text-lg text-gray-800 dark:text-gray-200">{selectedOrder.weekday}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Hora</p>
                  <p className="text-lg text-gray-800 dark:text-gray-200">{selectedOrder.hour}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 uppercase font-semibold">Data de Início</p>
                <p className="text-lg text-gray-800 dark:text-gray-200">{selectedOrder.date}</p>
              </div>

              {(() => {
                const ratings = RatingStore().getRatingsByComboId(selectedOrder.id);
                if (!ratings || ratings.length === 0) return null;
                return (
                  <div className="mt-2 p-4 rounded-xl bg-amber-50 dark:bg-[#2C3038] border border-amber-200 dark:border-gray-700">
                    <p className="text-sm text-amber-800 dark:text-yellow-300 uppercase font-semibold mb-3">
                      Avaliações do Cliente ({ratings.length})
                    </p>
                    {ratings.map((r, idx) => (
                      <div key={r.id || idx} style={{marginBottom: idx < ratings.length - 1 ? 10 : 0, paddingBottom: idx < ratings.length - 1 ? 10 : 0, borderBottom: idx < ratings.length - 1 ? '1px solid #fde68a' : 'none'}}>
                        {r.cleaningDate && (
                          <p className="text-xs text-amber-700 dark:text-yellow-200 mb-1">
                            Limpeza do dia {r.cleaningDate} 
                            <span className="mx-1">•</span> 
                            <span className={r.teamName ? 'font-semibold' : 'italic'}>{r.teamName || 'Sem equipe'}</span>
                          </p>
                        )}
                        <div className="flex items-center gap-2 mb-1">
                          <span style={{fontSize: 20, color: '#F8BB03', letterSpacing: 2}}>{'★'.repeat(r.rate)}{'☆'.repeat(5 - r.rate)}</span>
                          <span className="text-amber-800 dark:text-yellow-200 font-semibold text-sm">{r.rate}/5</span>
                        </div>
                        {r.feedback && (
                          <p className="text-sm text-amber-900 dark:text-gray-300 italic">"{r.feedback}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {(() => {
                const teams = TeamStore().getTeamsByComboId(selectedOrder.id);
                if (!teams || teams.length === 0) return null;
                return (
                  <div className="mt-2 p-4 rounded-xl bg-blue-50 dark:bg-[#1E3A8A] border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-300 uppercase font-semibold mb-3">
                      Equipes Alocadas ({teams.length})
                    </p>
                    {teams.map((t, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm mb-1 pb-1 border-b border-blue-100 last:border-0 last:pb-0 last:mb-0">
                        <span className="text-blue-900 dark:text-gray-200">Limpeza do dia {t.cleaningDate}</span>
                        <span className="font-bold text-blue-800 dark:text-blue-300">{t.teamName}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="flex gap-3 mt-6">
              {selectedOrder.status !== 'CANCELED' ? (
                <button 
                  onClick={(e) => handleCancel(e)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-red-200"
                >
                  Cancelar Pedido
                </button>
              ) : (
                <button 
                  onClick={(e) => handleReactivate(e)}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-green-200"
                >
                  Reativar Pedido
                </button>
              )}
              <button 
                onClick={() => setSelectedOrder(null)}
                className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-bold py-3 rounded-xl transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
